/* KrakenTheMeta — shared UI kit (the dashboard and every rebuilt public page).
 * One namespace, KD: helpers, toasts, sheets (dialogs / bottom sheets), confirm/ask, menus, busy state.
 * Anything user-generated goes through KD.esc() before it touches innerHTML.
 * Bundled by tools/build-php.cjs (esbuild): import this first.
 */
(function () {
  "use strict";

  var KD = (window.KD = window.KD || {});
  if (KD.uiReady) return;
  KD.uiReady = true;

  // Site root for links and legacy assets: same-origin on the live site, absolute when previewed from disk.
  KD.SITE = location.protocol === "file:" ? "https://krakenthemeta.com" : "";
  // Asset base from the bundle's own URL (/assets/js/<bundle>.js), so pages work from any path or from disk.
  KD.ASSETS = (function () {
    var s = document.currentScript && document.currentScript.src;
    return s ? s.replace(/js\/[\w.-]+\.js(\?.*)?$/, "") : "assets/";
  })();

  // ---------------------------------------------------------------- helpers
  KD.$ = function (s, r) { return (r || document).querySelector(s); };
  KD.$$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  KD.esc = function (s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  };
  KD.delay = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
  KD.fmt = function (n) { return Number(n || 0).toLocaleString("en-US"); };
  KD.money = function (n) { var v = Number(n) || 0; return "$" + v.toFixed(v >= 1000 ? 0 : 2); };
  KD.icon = function (name, cls) { return '<svg class="i' + (cls ? " " + cls : "") + '" aria-hidden="true"><use href="#i-' + name + '"/></svg>'; };
  KD.ga = function (name, params) { try { if (typeof window.gtag === "function") window.gtag("event", name, params || {}); } catch (e) { /* optional */ } };
  KD.safeUrl = function (u) { u = String(u || ""); return /^https:\/\//i.test(u) || u.charAt(0) === "/" ? u : ""; };
  KD.mobile = function () { return window.matchMedia("(max-width: 640px)").matches; };
  KD.reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  KD.plural = function (n, one, many) { return n === 1 ? one : (many || one + "s"); };
  KD.ago = function (d) {
    if (!d) return "";
    var s = (Date.now() - d.getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return Math.floor(s / 60) + "m ago";
    if (s < 86400) return Math.floor(s / 3600) + "h ago";
    if (s < 86400 * 7) return Math.floor(s / 86400) + "d ago";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
  };
  KD.toDate = function (ts) {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate();
    if (typeof ts === "number") return new Date(ts);
    if (typeof ts === "string") { var d = new Date(ts); return isNaN(d) ? null : d; }
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return null;
  };
  KD.img = function (u) { u = String(u || ""); return u.charAt(0) === "/" ? KD.SITE + u : u; };
  // The six stock avatars are ~500 KB PNGs; show the 160px WebP copies (the stored path stays the same).
  KD.avatar = function (u) {
    var m = /^\/images\/avatar([1-6])\.png$/.exec(String(u || ""));
    return m ? KD.ASSETS + "img/avatars/avatar" + m[1] + ".webp" : KD.img(u || "/images/avatar1.png");
  };
  // Mana symbols ({2}{G/U}{W}) as the site's symbol SVGs.
  KD.mana = function (cost, cls) {
    var out = "", m, re = /\{([^}]+)\}/g;
    while ((m = re.exec(cost || ""))) {
      out += '<img class="pip' + (cls ? " " + cls : "") + '" src="' + KD.ASSETS + "img/mana/" + encodeURIComponent(m[1].replace(/\//g, "")) +
        '.svg" alt="{' + KD.esc(m[1]) + '}" loading="lazy" decoding="async" onerror="this.remove()">';
    }
    return out;
  };
  KD.colorPips = function (colors) {
    return (colors || []).filter(function (c) { return /^[WUBRGC]$/.test(c); }).map(function (c) { return KD.mana("{" + c + "}"); }).join("");
  };
  KD.download = function (filename, text) {
    var url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    var a = document.createElement("a");
    a.href = url; a.download = filename; a.style.display = "none";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 1500);
  };
  // Clipboard with a fallback for older browsers and non-secure previews.
  KD.copy = function (text, okMsg) {
    var done = function () { KD.toast(okMsg || "Copied.", "ok"); };
    var fail = function () { KD.toast("Couldn't copy. Select the text and copy it yourself.", "err"); };
    var fallback = function () {
      var ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand("copy"); } catch (e) { /* unsupported */ }
      ta.remove();
      if (ok) done(); else fail();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, fallback);
    else fallback();
  };

  // Layer + toast hosts: the page may provide #layer / #toasts; otherwise they are created on demand.
  function host(id, cls) {
    var el = document.getElementById(id);
    if (!el) { el = document.createElement("div"); el.id = id; el.className = cls; document.body.appendChild(el); }
    return el;
  }

  // ---------------------------------------------------------------- toasts
  KD.toast = function (msg, kind) {
    kind = kind || "info";
    var h = host("toasts", "toasts");
    h.setAttribute("aria-live", "polite");
    var t = document.createElement("div");
    t.className = "toast toast--" + kind;
    t.setAttribute("role", kind === "err" ? "alert" : "status");
    t.innerHTML = KD.icon(kind === "ok" ? "check" : kind === "err" ? "alert" : "info") + "<p>" + KD.esc(msg) + "</p>";
    h.appendChild(t);
    while (h.children.length > 3) h.removeChild(h.firstChild);
    setTimeout(function () { t.classList.add("is-out"); setTimeout(function () { t.remove(); }, 320); }, kind === "err" ? 6000 : 4200);
  };

  // ---------------------------------------------------------------- sheets (dialogs / bottom sheets)
  var openSheets = [];
  KD.sheet = function (opts) {
    var layer = host("layer", "layer");
    var back = document.createElement("div");
    back.className = "backdrop";
    var el = document.createElement("div");
    el.className = "sheet" + (opts.size === "lg" ? " sheet--lg" : "");
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.innerHTML = '<div class="sheet__grab"></div>' +
      '<div class="sheet__head"><div style="flex:1;min-width:0"><h3>' + opts.title + "</h3>" + (opts.sub ? "<p>" + opts.sub + "</p>" : "") + "</div>" +
      '<button class="iconbtn" type="button" data-close aria-label="Close">' + KD.icon("x") + "</button></div>" +
      '<div class="sheet__body">' + (opts.body || "") + "</div>" +
      (opts.foot ? '<div class="sheet__foot">' + opts.foot + "</div>" : "");
    layer.appendChild(back);
    layer.appendChild(el);
    var prevFocus = document.activeElement;
    var closed = false;
    var api = {
      el: el,
      body: KD.$(".sheet__body", el),
      close: function (value) {
        if (closed) return;
        closed = true;
        openSheets = openSheets.filter(function (s) { return s !== api; });
        el.classList.remove("is-open");
        back.classList.remove("is-open");
        setTimeout(function () { el.remove(); back.remove(); }, 280);
        if (!openSheets.length) document.body.style.overflow = "";
        if (prevFocus && prevFocus.focus) try { prevFocus.focus({ preventScroll: true }); } catch (e) { /* gone */ }
        if (opts.onClose) opts.onClose(value);
      }
    };
    openSheets.push(api);
    document.body.style.overflow = "hidden";
    back.addEventListener("click", function () { if (!opts.sticky) api.close(); });
    KD.$$("[data-close]", el).forEach(function (b) { b.addEventListener("click", function () { api.close(); }); });
    requestAnimationFrame(function () {
      back.classList.add("is-open");
      el.classList.add("is-open");
      var f = KD.$("[autofocus]", el) || KD.$("[data-close]", el);
      if (f && !KD.mobile()) try { f.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    });
    return api;
  };
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && openSheets.length) openSheets[openSheets.length - 1].close();
  });
  KD.closeSheets = function () { openSheets.slice().forEach(function (s) { s.close(); }); };

  // Confirm dialog. { title, body, confirm, danger, typeToConfirm } → Promise<boolean>
  KD.confirm = function (o) {
    return new Promise(function (resolve) {
      var typed = o.typeToConfirm;
      var s = KD.sheet({
        title: KD.esc(o.title),
        body: '<div class="stack">' + (o.body || "") +
          (typed ? '<label class="field"><span class="label">Type ' + KD.esc(typed) + ' to confirm</span><input class="input" data-typed autocomplete="off" spellcheck="false" placeholder="' + KD.esc(typed) + '"></label>' : "") +
          "</div>",
        foot: '<button class="btn btn--ghost" type="button" data-no>Cancel</button>' +
          '<button class="btn ' + (o.danger ? "btn--danger" : "btn--lure") + '" type="button" data-yes' + (typed ? " disabled" : "") + ">" + KD.esc(o.confirm || "Confirm") + "</button>",
        onClose: function (v) { resolve(v === true); }
      });
      var yes = KD.$("[data-yes]", s.el);
      if (typed) KD.$("[data-typed]", s.el).addEventListener("input", function (e) { yes.disabled = e.target.value.trim() !== typed; });
      KD.$("[data-no]", s.el).addEventListener("click", function () { s.close(false); });
      yes.addEventListener("click", function () { s.close(true); });
    });
  };

  // Text prompt. → Promise<string|null>
  KD.ask = function (o) {
    return new Promise(function (resolve) {
      var s = KD.sheet({
        title: KD.esc(o.title),
        body: '<label class="field"><span class="label">' + KD.esc(o.label || "") + '</span><input class="input" data-val maxlength="' + (o.max || 80) + '" value="' + KD.esc(o.value || "") + '" placeholder="' + KD.esc(o.placeholder || "") + '" autofocus></label>',
        foot: '<button class="btn btn--ghost" type="button" data-no>Cancel</button><button class="btn btn--lure" type="button" data-yes>' + KD.esc(o.confirm || "Save") + "</button>",
        onClose: function (v) { resolve(typeof v === "string" ? v : null); }
      });
      var input = KD.$("[data-val]", s.el);
      var done = function () { var v = input.value.trim(); if (v) s.close(v); };
      KD.$("[data-no]", s.el).addEventListener("click", function () { s.close(null); });
      KD.$("[data-yes]", s.el).addEventListener("click", done);
      input.addEventListener("keydown", function (e) { if (e.key === "Enter") done(); });
      setTimeout(function () { input.focus(); input.select(); }, 60);
    });
  };

  // Action menu: a popover on desktop, an action sheet on phones.
  // items: [{ label, icon, onClick, danger, href, external, rel } | "sep"]
  var openMenu = null;
  KD.menu = function (anchor, items, title) {
    if (openMenu) { openMenu.remove(); openMenu = null; }
    var html = items.map(function (it) {
      if (it === "sep") return "<hr>";
      var inner = (it.icon ? KD.icon(it.icon) : "") + "<span>" + KD.esc(it.label) + "</span>";
      var cls = it.danger ? ' class="is-danger"' : "";
      return it.href
        ? "<a" + cls + ' href="' + KD.esc(it.href) + '"' + (it.external ? ' target="_blank" rel="' + KD.esc(it.rel || "noopener") + '"' : "") + ">" + inner + "</a>"
        : "<button" + cls + ' type="button">' + inner + "</button>";
    }).join("");
    var actionable = items.filter(function (it) { return it !== "sep"; });
    var bind = function (root, close) {
      KD.$$("button, a", root).forEach(function (b, i) {
        b.addEventListener("click", function () {
          var it = actionable[i];
          close();
          if (it && it.onClick) it.onClick();
        });
      });
    };
    if (KD.mobile()) {
      var s = KD.sheet({ title: KD.esc(title || "Options"), body: '<div class="actionlist">' + html + "</div>" });
      bind(s.el.querySelector(".actionlist"), function () { s.close(); });
      return;
    }
    var m = document.createElement("div");
    m.className = "menu-pop";
    m.setAttribute("role", "menu");
    m.innerHTML = html;
    document.body.appendChild(m);
    var r = anchor.getBoundingClientRect();
    var w = m.offsetWidth, h = m.offsetHeight;
    var left = Math.min(window.innerWidth - w - 10, Math.max(10, r.right - w));
    var top = r.bottom + 6;
    if (top + h > window.innerHeight - 10) top = Math.max(10, r.top - h - 6);
    m.style.left = left + "px";
    m.style.top = top + "px";
    openMenu = m;
    var close = function () { if (openMenu === m) openMenu = null; m.remove(); document.removeEventListener("click", outside, true); };
    var outside = function (e) { if (!m.contains(e.target) && !anchor.contains(e.target)) close(); };
    setTimeout(function () { document.addEventListener("click", outside, true); }, 0);
    bind(m, close);
  };
  window.addEventListener("resize", function () { if (openMenu) { openMenu.remove(); openMenu = null; } });

  // Busy state for short operations.
  var busyCount = 0, busyToast = null;
  KD.busy = function (msg) {
    busyCount++;
    if (!busyToast) {
      busyToast = document.createElement("div");
      busyToast.className = "toast";
      busyToast.innerHTML = '<span class="spin"></span><p></p>';
      host("toasts", "toasts").appendChild(busyToast);
    }
    KD.$("p", busyToast).textContent = msg || "Working…";
  };
  KD.idle = function () {
    busyCount = Math.max(0, busyCount - 1);
    if (!busyCount && busyToast) { busyToast.remove(); busyToast = null; }
  };
  // progress text for the current busy state ("Matching card names… 40 of 96")
  KD.busyText = function (msg) { if (busyToast) KD.$("p", busyToast).textContent = msg; };
})();
